import { MoonIcon, SunIcon } from "@chakra-ui/icons";
import { Badge, Box, Link as ChakraLink } from "@chakra-ui/layout";
import {
  Avatar,
  Button,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { QuoteApi } from "../api/QuoteApi";
import useUser from "../hooks/useUser";
import { darkMode, sidebar } from "../lib/models/color-mode-colors";
import { SelectItem } from "../lib/types/SelectItem";
import { SidebarLink } from "../lib/types/SidebarLink";
import { UserAccount } from "../lib/types/UserAccountType";
import logo from "../logo-white.svg";
import { NavigationLinkService } from "../services/navbar.service";
import { LogoutIcon } from "./icons/LogoutIcon";
import { PinnedItems } from "./pinned-items";
import ShowForRole from "./ShowForRole";

export interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

function Sidebar(props: SidebarProps) {
  const { colorMode, toggleColorMode } = useColorMode();
  const { user, userPreferences, sfProfile, role } = useUser();
  const userContext = useUser();
  const [sidebarLinks, setSidebarLinks] = React.useState<SidebarLink[]>([]);
  const [pathWithQuery, setPathWithQuery] = React.useState<string>("");
  const { isOpen, onToggle } = props;
  const location = useLocation();
  const [userAccounts, setUserAccounts] = React.useState<UserAccount[]>([]);
  const sidebarBg = useColorModeValue(sidebar.light, darkMode[300]);

  React.useEffect(() => {
    const { pathname, search } = location;
    setPathWithQuery(`${pathname}${search}`);
  }, [location]);

  React.useEffect(() => {
    if (!role) return;

    let brokers = [];

    if (sfProfile?.brokers) {
      brokers.push(...sfProfile.brokers);
    }

    const links = NavigationLinkService.getSideBarLinksByRole(
      role,
      brokers.length
    );

    setSidebarLinks(links);
  }, [role, sfProfile]);

  const fetchUsers = React.useCallback(() => {
    QuoteApi.getQuoteUsers()
      .then((res) => {
        setUserAccounts(
          res.data.map((x) => {
            return x;
          })
        );
      })
      .catch((err) => {});
  }, []);

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const salesRepItems: SelectItem[] = React.useMemo(() => {
    if (!userAccounts) {
      return [];
    }
    return userAccounts.map((item) => ({
      label: `${item.firstName} ${item.lastName}`,
      value: item.userID.toString(),
    }));
  }, [userAccounts]);

  const brokerAccountItems: SelectItem[] = React.useMemo(() => {
    if (!sfProfile?.brokers) {
      return [];
    }
    return sfProfile.brokers.map((item) => ({
      label: item.name,
      value: item.accountid,
    }));
  }, [sfProfile?.brokers]);

  const borrowerAccountItems: SelectItem[] = React.useMemo(() => {
    return userContext.sfAccounts.map((item) => ({
      label: item.name,
      value: item.accountid,
    }));
  }, [userContext.sfAccounts]);

  return (
    <>
      <StyledSidebar
        colorMode={colorMode}
        sidebarBg={sidebarBg}
        className={isOpen ? "sidenav-open" : ""}
      >
        <SidebarInnerWrapper>
          <SidebarLogoAndLinks>
            <ChakraLink
              as={Link}
              to="/quotes/my-quotes"
              display="flex"
              paddingTop={8}
              justifyContent="center"
              _focus={{ boxShadow: "none" }}
            >
              <img
                width="155"
                height="auto"
                src={logo}
                alt="Lima One Capital Logo"
              />
            </ChakraLink>
            <nav>
              <ul>
                {sidebarLinks.map((link) => (
                  <li
                    className={`sidenav-item ${
                      pathWithQuery === link.path ? "active" : ""
                    }`}
                    key={link.label}
                  >
                    {React.createElement(link.icon, {
                      className: "sidenav-icon",
                    })}
                    <Link className="sidenav-link" to={link.path}>
                      <Box display="flex" justifyContent="space-between">
                        {link.label}

                        {link.badge && (
                          <Badge
                            alignSelf="center"
                            fontSize="sm"
                            paddingX={1.5}
                            color="#014174"
                            backgroundColor="#5a97c8"
                            marginRight={4}
                          >
                            {link.badge}
                          </Badge>
                        )}
                      </Box>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <Box marginTop={4}>
              <ShowForRole
                roles={[
                  "Broker",
                  "Broker Manager",
                  "Lender Finance",
                  "Lender Finance Manager",
                  "Wholesale",
                  "Wholesale Manager",
                  "Business Development",
                  "Business Development Manager",
                  "Inside Sales",
                  "Inside Sales Manager",
                  "Sales",
                ]}
              >
                <PinnedItems
                  items={brokerAccountItems}
                  selectedItemValues={
                    userPreferences?.preferences?.pinnedBrokerAccountIds ?? []
                  }
                  label={"Brokers"}
                  savePreferencesKey={"pinnedBrokerAccountIds"}
                  pathValue={"brokers"}
                />
              </ShowForRole>

              <ShowForRole
                roles={[
                  "Production Coordinator",
                  "Operations Analyst",
                  "Sales Assistant",
                  "Capital Markets",
                  "Broker Manager",
                  "Lender Finance Manager",
                  "Wholesale Manager",
                  "Business Development Manager",
                  "Inside Sales Manager",
                ]}
              >
                <PinnedItems
                  items={salesRepItems}
                  selectedItemValues={
                    userPreferences?.preferences?.pinnedUserIds ?? []
                  }
                  label={"Sales Reps"}
                  savePreferencesKey={"pinnedUserIds"}
                  pathValue={"users"}
                />
              </ShowForRole>

              <ShowForRole
                roles={[
                  "Broker",
                  "Broker Manager",
                  "Lender Finance",
                  "Lender Finance Manager",
                  "Wholesale",
                  "Wholesale Manager",
                  "Business Development",
                  "Business Development Manager",
                  "Inside Sales",
                  "Inside Sales Manager",
                  "Sales",
                ]}
              >
                <PinnedItems
                  items={borrowerAccountItems}
                  selectedItemValues={
                    userPreferences?.preferences?.pinnedBorrowerIds ?? []
                  }
                  label={"Borrowers"}
                  savePreferencesKey={"pinnedBorrowerIds"}
                  pathValue={"borrowers"}
                />
              </ShowForRole>
            </Box>
          </SidebarLogoAndLinks>

          {user && (
            <SidebarProfile>
              <div className="avatar-username-container">
                <Popover trigger="hover">
                  <PopoverTrigger>
                    <Avatar
                      cursor={"pointer"}
                      color="brand.50"
                      backgroundColor="#2073BB"
                      size="sm"
                      name={user.name}
                    />
                  </PopoverTrigger>
                  <PopoverContent width={"50px"}>
                    <PopoverArrow />
                    <Button variant={"link"} onClick={toggleColorMode}>
                      {colorMode === "light" ? (
                        <MoonIcon
                          marginY={2}
                          h={6}
                          w={6}
                          color={darkMode[300]}
                        />
                      ) : (
                        <SunIcon marginY={2} h={6} w={6} color={"white"} />
                      )}
                    </Button>
                  </PopoverContent>
                </Popover>
                <div className="username-container">
                  <span className="username">{user.name}</span>
                  <span className="role">{role}</span>
                </div>
              </div>

              <Button
                title="Sign Out"
                as={Link}
                to={`/logout`}
                variant="ghost"
                padding={0}
                _hover={{ backgroundColor: "hsla(208, 71%, 35%, 0.5)" }}
              >
                <LogoutIcon color="brand.50" height={6} width={6} />
              </Button>
            </SidebarProfile>
          )}
        </SidebarInnerWrapper>
      </StyledSidebar>
      {isOpen && (
        <StyledSidebarBackdrop onClick={onToggle}></StyledSidebarBackdrop>
      )}
    </>
  );
}

const SidebarInnerWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
`;

const SidebarLogoAndLinks = styled.div``;

const SidebarProfile = styled.div`
  display: flex;
  align-items: center;
  border-top: 1px solid #0f3457;
  padding: 1.25rem 0.75rem;
  justify-content: space-between;

  .avatar-username-container {
    display: flex;
    align-items: center;
  }

  .username-container {
    display: flex;
    flex-direction: column;
    margin-left: 1rem;

    .username {
      font-size: 1rem;
      color: #c8e6fe;
    }

    .role {
      font-size: 0.9rem;
      color: #7abef5;
    }
  }
`;

const StyledSidebarBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.25);
  z-index: 300;
  display: none;

  @media (max-width: 1280px) {
    display: block;
  }
`;

const StyledSidebar = styled.div<{ sidebarBg: string; colorMode: string }>`
  background: ${(props) => props.sidebarBg};
  border-right: ${(props) =>
    props.colorMode === "light" ? "none" : "1px solid #0F3457"};
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 275px;
  z-index: 301;

  @media (max-width: 1280px) {
    display: none;

    &.sidenav-open {
      display: block;
      width: 275px;
    }
  }

  nav {
    padding: 2.5rem 0 2.5rem 1rem;
  }

  ul {
    list-style: none;
    margin: 0;
  }

  .sidenav-item {
    display: flex;
    align-items: center;
    color: #7abef5;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    padding-left: 1rem;
    margin-right: 1rem;
    margin-bottom: 0.25rem;
    transition: color 0.2s ease-in-out;

    &.active {
      color: #c8e6fe;
      border-radius: 6px;
      background: ${(props) =>
        props.colorMode === "light"
          ? "hsla(208, 71%, 35%, 0.6)"
          : "hsla(208, 71%, 35%, 0.2)"};
    }

    &:hover {
      border-radius: 6px;
      background: ${(props) =>
        props.colorMode === "light"
          ? "hsla(208, 71%, 35%, 0.2)"
          : "hsla(208, 71%, 35%, 0.05)"};
    }

    .sidenav-link {
      flex: 1;
    }

    .sidenav-icon {
      height: 1.25rem;
      width: 1.25rem;
      margin-right: 1rem;
    }
  }
`;

export default Sidebar;
